import { useState } from "react";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, Trophy } from "lucide-react";
import { FUNNELS } from "../../data/funnelContent";
import { CONGRATS } from "../../data/congratsFunnel";
import { WEB_FUNNELS } from "../../data/webDesignFunnel";
import { WebInquiriesPanel } from "./WebInquiriesPanel";
import { WebReviewsPanel } from "./WebReviewsPanel";
import { useUrlAnalytics } from "./AdminUrlAnalyticsTab";

const copy = (text, label) => navigator.clipboard.writeText(text).then(() => toast.success(`${label} skopírované`));
const FORMATS = [
  { key: "square", label: "FB/IG príspevok 1:1 (1080×1080)", cls: "aspect-square" },
  { key: "story", label: "IG/FB Story 9:16 (1080×1920)", cls: "aspect-[9/16]" },
  { key: "google", label: "Google 1.91:1 (1200×628)", cls: "aspect-[1.91/1]" },
];
const EMPTY = { unique_visitors: 0, unique_clickers: 0, cvr_pct: 0 };
const pathOf = (f) => f.path || `/riesenia/${f.slug}`;
const adUrl = (f, source) => `${pathOf(f)}?utm_source=${source}&utm_medium=${source === "google" ? "cpc" : "paid_social"}&utm_campaign=${f.path ? f.slug : `allin2026_${f.slug}`}`;

const Btn = ({ onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold border border-gray-300 rounded hover:bg-gray-50">{children}</button>
);

const FunnelCard = ({ funnel, n, stats, isWinner }) => {
  const [open, setOpen] = useState(false);
  const origin = window.location.origin;
  const { meta, google } = funnel.ads;
  return (
    <div className={`bg-white border rounded-lg ${isWinner ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200"}`} data-testid={`funnel-card-${funnel.slug}`}>
      <div className="p-4 flex flex-wrap items-center gap-4">
        <span className="w-9 h-9 rounded-full bg-gray-900 text-white font-black flex items-center justify-center">{n}</span>
        <div className="flex-1 min-w-[180px]">
          <p className="font-bold text-gray-900">{funnel.label} {isWinner && <Trophy className="inline w-4 h-4 text-emerald-500" />}</p>
          <p className="text-xs text-gray-500">{funnel.type} · {pathOf(funnel)}</p>
        </div>
        <div className="flex gap-4 text-center text-xs" data-testid={`funnel-stats-${funnel.slug}`}>
          <div><p className="text-gray-500 uppercase text-[10px]">Návštevy</p><p className="font-bold">{stats.unique_visitors}</p></div>
          <div><p className="text-gray-500 uppercase text-[10px]">Kliky</p><p className="font-bold">{stats.unique_clickers}</p></div>
          <div><p className="text-gray-500 uppercase text-[10px]">Konverzia</p><p className="font-bold">{stats.cvr_pct} %</p></div>
        </div>
        <a href={pathOf(funnel)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`funnel-open-${funnel.slug}`}>Stránka <ExternalLink className="w-3 h-3" /></a>
        <button onClick={() => setOpen(!open)} className="px-3 py-1.5 text-xs font-bold bg-gray-900 text-white rounded" data-testid={`funnel-toggle-${funnel.slug}`}>{open ? "Skryť" : "Reklamy"}</button>
      </div>
      {open && (
        <div className="border-t border-gray-100 p-4 space-y-5" data-testid={`funnel-ads-${funnel.slug}`}>
          <div className="grid grid-cols-3 gap-3">
            {FORMATS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <img src={funnel.images[f.key]} alt={f.label} className={`w-full ${f.cls} object-cover rounded`} />
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[11px] text-gray-500">{f.label}</span>
                  <a href={funnel.images[f.key]} download target="_blank" rel="noreferrer" className="text-gray-600 hover:text-gray-900" data-testid={`funnel-download-${funnel.slug}-${f.key}`}><Download className="w-4 h-4" /></a>
                </div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="rounded border border-gray-200 p-3 space-y-2">
              <p className="text-xs font-black uppercase text-blue-700">Facebook + Instagram</p>
              <p className="text-gray-800">{meta.primary}</p>
              <p className="font-bold">{meta.headline}</p>
              <p className="text-xs text-gray-500">{meta.description}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Btn onClick={() => copy(`${meta.primary}\n\n${meta.headline}\n${meta.description}`, "Text")} testid={`funnel-copy-meta-${funnel.slug}`}><Copy className="w-3 h-3" /> Text</Btn>
                <Btn onClick={() => copy(origin + adUrl(funnel, "facebook"), "Odkaz FB")} testid={`funnel-copy-fb-url-${funnel.slug}`}><Copy className="w-3 h-3" /> Odkaz FB</Btn>
                <Btn onClick={() => copy(origin + adUrl(funnel, "instagram"), "Odkaz IG")} testid={`funnel-copy-ig-url-${funnel.slug}`}><Copy className="w-3 h-3" /> Odkaz IG</Btn>
              </div>
            </div>
            <div className="rounded border border-gray-200 p-3 space-y-2">
              <p className="text-xs font-black uppercase text-emerald-700">Google Ads (responzívna reklama)</p>
              <ol className="list-decimal list-inside text-gray-800">{google.headlines.map((h) => <li key={h}>{h} <span className="text-[10px] text-gray-400">({[...h].length}/30)</span></li>)}</ol>
              {google.descriptions.map((d) => <p key={d} className="text-xs text-gray-600">{d} <span className="text-[10px] text-gray-400">({[...d].length}/90)</span></p>)}
              <div className="flex flex-wrap gap-2 pt-1">
                <Btn onClick={() => copy(`${google.headlines.join("\n")}\n\n${google.descriptions.join("\n")}`, "Texty Google")} testid={`funnel-copy-google-${funnel.slug}`}><Copy className="w-3 h-3" /> Texty</Btn>
                <Btn onClick={() => copy(origin + adUrl(funnel, "google"), "Odkaz Google")} testid={`funnel-copy-google-url-${funnel.slug}`}><Copy className="w-3 h-3" /> Odkaz Google</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminFunnelCampaignsTab() {
  const { data } = useUrlAnalytics("/riesenia", 30);
  const { data: gData } = useUrlAnalytics("/gratulujeme", 30);
  const { data: wData } = useUrlAnalytics("/tvorba-webu", 30);
  const byPage = Object.fromEntries([...(data?.rows || []), ...(gData?.rows || []), ...(wData?.rows || [])].map((r) => [r.page, r]));
  const statsFor = (f) => byPage[pathOf(f)] || EMPTY;
  const best = FUNNELS.reduce((w, f) => (statsFor(f).cvr_pct > (w ? statsFor(w).cvr_pct : 0) ? f : w), null);
  return (
    <div className="space-y-3" data-testid="admin-funnel-campaigns-tab">
      <p className="text-xs text-gray-500">{FUNNELS.length} funnelov pre firmy predáva plán Všetko v jednom 2026 (€79/mes alebo €590 do 31. 12. 2026). Štatistiky za 30 dní. Každý funnel má 3 obrázky a texty pre Facebook, Instagram aj Google.</p>
      {FUNNELS.map((f, i) => <FunnelCard key={f.slug} funnel={f} n={i + 1} stats={statsFor(f)} isWinner={best?.slug === f.slug} />)}
      <h3 className="pt-4 text-sm font-black text-gray-900" data-testid="congrats-campaigns-heading">Funnel „Gratulujeme!“ · 3 varianty pre SK a CZ (sken webu do pár sekúnd)</h3>
      {CONGRATS.map((f, i) => <FunnelCard key={f.slug} funnel={f} n={FUNNELS.length + i + 1} stats={statsFor(f)} isWinner={false} />)}
      <h3 className="pt-4 text-sm font-black text-gray-900" data-testid="web-campaigns-heading">Funnel „Tvorba webu €499“ · 3 varianty pre SK a CZ (dopyt a návrh zadarmo)</h3>
      <WebInquiriesPanel />
      <WebReviewsPanel />
      {WEB_FUNNELS.map((f, i) => <FunnelCard key={f.slug} funnel={f} n={FUNNELS.length + CONGRATS.length + i + 1} stats={statsFor(f)} isWinner={false} />)}
    </div>
  );
}
